<template>
  <div>
    <PriceEditionDialog v-model="showPriceEditionDialog" :initialPrice="Number(lastEditedPriceData.price)"
      @changePrice="handleChangePrice" />

    <v-expansion-panel-header class="text-h3">
      {{ (isRateModel2024 || isRateModel2025 || isRateModel2026 || isRateModel2026V2) && id === "24H" ? $t(`PRODUCT_${id}`) + " / " + $t(`PRODUCT_C14E`) : $t(`PRODUCT_${desc}`) }}

      <div>
        <ProductChip v-if="isMaxDiscountLimitExceeded && this.offerType !== 'C'" type="warning"
          :label="$t('SHIPPING_MAX_DISCOUNTS_TAG')" />

        <ProductChip v-if="!areDefaultWeights && this.offerType !== 'C'" type="warning"
          :label="$t('SHIPPING_MODIFIED_WEIGHTS_TAG')" />

        <ProductChip v-if="isOfferInvalidForPrices" type="error" :label="$t('SHIPPING_INVALID_DISCOUNTS_TAG')" />
      </div>
    </v-expansion-panel-header>

    <v-expansion-panel-content>
      <v-data-table :headers="productHeaders" :items="productTable.kgRows" :hide-default-footer="true"
        class="mb-8 text-body-2" :mobile-breakpoint="0" disable-pagination>
        <template v-slot:header.weight>
          <UpdateSelectedWeightsDialog :productId="id" :productWeightList="productWeightList"
            :currentWeightList="currentWeightList" :standardWeightList="standardWeightList" />
        </template>

        <template v-slot:body>
          <tbody slot="body">
            <tr v-for="(row, rowIndex) in productTable.kgRows" :key="`row${rowIndex}`"
              :data-cy="`row-weight-price-${rowIndex}`">
              <v-tooltip v-for="(value, zoneId) in row" :key="zoneId" :disabled="isTooltipDisabled(row, zoneId)"
                color="#fff2f4" bottom>
                <template v-slot:activator="{ on }">
                  <td v-on="activeTooltip(on, row, zoneId)" @click.stop="
                    canEditOffer &&
                    zoneId !== 'weight' &&
                    offerType === 'C' &&
                    handleShowPriceEditionDialog('kg', value, zoneId, row.weight)
                    " class="text-body-2"
                    :class="[zoneId !== 'weight' && 'text-center', getCellExtraClass(row, zoneId)]">
                    {{ value }}
                  </td>
                </template>

                <div class="shipping__tooltip">
                  <span class="shipping__tooltip-text" v-html="tooltipText(row, zoneId)" />
                </div>
              </v-tooltip>
            </tr>

            <tr v-if="productTable.addKilo">
              <v-tooltip v-for="(value, key) in productTable.addKilo" :key="key"
                :disabled="isTooltipDisabled(productTable.addKilo, key)" color="#fff2f4" bottom>
                <template v-slot:activator="{ on }">
                  <td @click.stop="
                    offerType === 'C' && handleShowPriceEditionDialog('addKg', value, key)
                    " v-on="activeTooltip(on, productTable.addKilo, key)" :key="'additKg-' + key" :class="[
                      'text-body-2',
                      key !== 'weight' && 'text-center',
                      getCellExtraClass(productTable.addKilo, key),
                    ]" class="light-grey-bckgr" :data-cy="`shipping-weight-${key}`">
                    {{ value }}
                  </td>
                </template>

                <div class="shipping__tooltip">
                  <span class="shipping__tooltip-text" v-html="tooltipText(productTable.addKilo, key)" />
                </div>
              </v-tooltip>
            </tr>

            <slot name="discounts" />
          </tbody>
        </template>
      </v-data-table>
    </v-expansion-panel-content>
  </div>
</template>

<script>
import UpdateSelectedWeightsDialog from '@/containers/offer-form/shipping/UpdateSelectedWeightsDialog'
import PriceEditionDialog from '@/containers/offer-form/shipping/PriceEditionDialog'
import ProductChip from '@/containers/offer-form/shipping/ProductChip'
import { mapGetters, mapMutations } from 'vuex'
import {
  GET_OFFER_ITEM,
  GET_PRODUCT_TABLE,
  GET_PRODUCT_WEIGHT_LIST,
  GET_PRODUCT_CURRENT_WEIGHT_LIST,
  GET_PRODUCT_STANDARD_WEIGHT_LIST,
  GET_PRODUCT,
} from '@/store/modules/offer/getters'
import { UPDATE_PRODUCT_ZONE_KG_PRICE } from '@/store/modules/offer/mutations'

export default {
  name: 'ProductTable',
  components: {
    UpdateSelectedWeightsDialog,
    PriceEditionDialog,
    ProductChip,
  },
  props: {
    id: {
      type: String,
    },
    desc: {
      type: String,
    },
    listMaxDiscountLimitExceededPerZone: {
      type: Object,
    },
    isMaxDiscountLimitExceeded: {
      type: Boolean,
    },
    isOfferInvalidForPrices: {
      type: Boolean,
    },
    areDefaultWeights: {
      type: Boolean,
    },
    validationErrors: {
      type: Object,
    },
  },
  data() {
    return {
      baseHeader: {
        text: `${this.$t('WEIGHT')} (kg)`,
        value: 'weight',
        width: '120px',
        align: 'start',
        class: 'text-body-2',
        sortable: false,
        product: this.productTable,
      },
      showPriceEditionDialog: false,
      lastEditedPriceData: {
        weight: null,
        zoneId: null,
        price: null,
        typeKg: null,
      },
    }
  },
  computed: {
    ...mapGetters('offer', [
      GET_OFFER_ITEM,
      GET_PRODUCT_TABLE,
      GET_PRODUCT_WEIGHT_LIST,
      GET_PRODUCT_CURRENT_WEIGHT_LIST,
      GET_PRODUCT_STANDARD_WEIGHT_LIST,
      GET_PRODUCT,
      'canEditOffer',
    ]),
    offerType() {
      return this[GET_OFFER_ITEM]('type')
    },
    isRateModel2024() {
      return this[GET_OFFER_ITEM]('rateModelDesc')?.contractModel === "PDF_VENTA_DIRECTA_2024"
    },
    isRateModel2025() {
      return this[GET_OFFER_ITEM]('rateModelDesc')?.contractModel === "PDF_VENTA_DIRECTA_2025"
    },
    isRateModel2026() {
      return this[GET_OFFER_ITEM]('rateModelDesc')?.contractModel === "PDF_VENTA_DIRECTA_2026"
    },
    isRateModel2026V2() {
      return this[GET_OFFER_ITEM]('rateModelDesc')?.contractModel === "PDF_VENTA_DIRECTA_2026_V2"
    },
    productTable() {
      return this[GET_PRODUCT_TABLE](this.id)
    },
    productHeaders() {
      return [this.baseHeader].concat(
        this.productTable.headers.map(headerId => this.getProductHeader(headerId)),
      )
    },
    productWeightList() {
      return this[GET_PRODUCT_WEIGHT_LIST](this.id)
    },
    currentWeightList() {
      return this[GET_PRODUCT_CURRENT_WEIGHT_LIST](this.id)
    },
    standardWeightList() {
      return this[GET_PRODUCT_STANDARD_WEIGHT_LIST](this.id)
    },
  },
  methods: {
    ...mapMutations('offer', [UPDATE_PRODUCT_ZONE_KG_PRICE]),
    getProductHeader(headerId) {
      return {
        ...this.baseHeader,
        align: 'center',
        text: `${this.$t(this.getZoneHeaderKey(headerId))} (€)`,
        value: headerId,
      }
    },
    getZoneHeaderKey(zoneId) {
      // Prefer product-provided desc_key to preserve original visual labels (e.g., with suffixes like (C))
      const product = this[GET_PRODUCT](this.id)
      const zone = product?.zoneList?.find(z => z.id === zoneId)
      return zone?.desc_key || `ZONE_${zoneId}`
    },
    getCellExtraClass(row, zoneId) {
      if (this.cellInvalid(row, zoneId)) {
        return 'shipping__error'
      }

      if (this.listMaxDiscountLimitExceededPerZone[zoneId] && this.offerType !== 'C') {
        return 'shipping__warning'
      }

      return ''
    },
    activeTooltip(on, row, zoneId) {
      if (this.cellInvalid(row, zoneId)) {
        return on
      }
    },
    tooltipText(row, zoneId) {
      return this.validationErrors?.[zoneId]?.[row.weight]
        ?.map(errorObject => errorObject.text)
        .join('<br />------<br />')
    },
    cellInvalid(row, zoneId) {
      const zoneError = this.validationErrors?.[zoneId] ?? {}
      return Object.keys(zoneError).includes(row.weight.toString())
    },
    isTooltipDisabled(row, zoneId) {
      return (
        !this.validationErrors ||
        !(zoneId in this.validationErrors) ||
        Object.keys(this.validationErrors[zoneId]).length === 0
      )
    },
    handleShowPriceEditionDialog(typeKg, price, zoneId, weight) {
      this.lastEditedPriceData = {
        weight: typeKg === 'addKg' ? this.$t('SHIPPING_ADDITIONAL_KG') : weight,
        zoneId,
        price,
        typeKg,
      }
      this.showPriceEditionDialog = true
    },
    handleChangePrice(price) {
      this[UPDATE_PRODUCT_ZONE_KG_PRICE]({
        productId: this.id,
        ...this.lastEditedPriceData,
        price,
      })
      this.$emit('updatePrice')
      if (this.id === '24H') {
        this.$root.$emit('priceUpdatedFor24H', {
          ...this.lastEditedPriceData,
          price,
        });
      }
    },
  },
}
</script>

<style scoped lang="scss">
td.text-end {
  min-width: 149px;
}

.light-grey-bckgr {
  background-color: $light-grey;
}

.shipping {
  &__tooltip {
    width: rem(140);

    &-text {
      color: #000;
    }
  }

  &__error {
    background-color: rgb(223, 0, 36, 0.12);
  }

  &__warning {
    background-color: rgb(254, 246, 204);
  }

  &__field {
    width: rem(110);

    &--right {
      margin-left: auto;
    }
  }
}
</style>
